import { getTemplateRatio } from "~/common/videoTemplate/template"
import { useLocation, useNavigate } from "react-router"
import { getTemplateDetailPath } from "~/common/router"
import { useContext, useEffect, useRef, useState, type ReactNode } from "react"
import { Button, Empty, Flex, Masonry, Result, Spin, message } from "antd"
import classNames from "classnames"
import { consumeOneClickCopyRetryTransfer } from "~/common/videoTemplate/retry"
import { useAnalyticsEvent, Text, SparklesIcon, Tag } from "~/components"
import { VideoDownloadPolicy } from "~/components/VideoPlayer"
import { PreviewModal } from "./PreviewModal"
import { CARD_GAP, useInspirationColumnCount } from "./helpers"
import {
  getTemplateAnalyticsFields,
  getTemplateBadge,
  type VideoTemplate,
} from "~/common/videoTemplate"
import { TemplateFilters } from "./TemplateFilters"
import { useTemplateGallery } from "./useTemplateGallery"
import { LoadMore } from "./LoadMore"
import styles from "./index.css"

const hoverPlayDelay = 200
const lazyLoadRootMargin = "320px 0px"
const getInspirationCardElementId = (cardId: string) => {
  return `inspiration-card-${cardId}`
}

type InspirationCardItemProps = {
  card: VideoTemplate
  onPreview: (card: VideoTemplate) => void
  action?: ReactNode
}

const InspirationCardItem = ({
  card,
  onPreview,
  action,
}: InspirationCardItemProps) => {
  const restrictDownload = useContext(VideoDownloadPolicy)
  const cardRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const hoverTimerRef = useRef<number | null>(null)
  const isHoveringRef = useRef(false)
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const [isVideoReady, setIsVideoReady] = useState(false)
  const [canShowVideo, setCanShowVideo] = useState(false)
  const [coverFailed, setCoverFailed] = useState(false)
  const [videoFailed, setVideoFailed] = useState(false)
  const badge = getTemplateBadge(card.categories)
  const hasVideoCover = Boolean(card.videoCover && !coverFailed)
  const shouldPlayVideo = Boolean(
    card.videoUrl &&
    shouldLoadVideo &&
    isHovering &&
    canShowVideo &&
    (!hasVideoCover || isVideoReady),
  )
  const showVideo = Boolean(
    card.videoUrl &&
    !videoFailed &&
    shouldLoadVideo &&
    (!hasVideoCover || shouldPlayVideo),
  )
  const showCover = Boolean(hasVideoCover && !showVideo)

  const clearHoverTimer = () => {
    if (!hoverTimerRef.current) return

    window.clearTimeout(hoverTimerRef.current)
    hoverTimerRef.current = null
  }

  const playVideo = (reset = false) => {
    if (!videoRef.current || !isHoveringRef.current) return

    if (reset && videoRef.current.readyState > 0) {
      try {
        videoRef.current.currentTime = 0
      } catch {
        // Some browsers disallow seeking before metadata is ready.
      }
    }

    void videoRef.current.play().catch(() => {
      // A blocked autoplay must restore the poster and keep the card usable.
      setCanShowVideo(false)
    })
  }

  const pauseVideo = () => {
    if (!videoRef.current) return

    videoRef.current.pause()

    if (videoRef.current.readyState > 0) {
      try {
        videoRef.current.currentTime = 0
      } catch {
        // Ignore interrupted hover resets.
      }
    }
  }

  const schedulePlayVideo = () => {
    clearHoverTimer()
    isHoveringRef.current = true
    setIsHovering(true)
    setCanShowVideo(false)

    if (card.videoUrl && !shouldLoadVideo) {
      setShouldLoadVideo(true)
    }

    hoverTimerRef.current = window.setTimeout(() => {
      hoverTimerRef.current = null
      setCanShowVideo(true)
    }, hoverPlayDelay)
  }

  const markVideoReady = () => {
    setIsVideoReady(true)
  }

  const handleMouseLeave = () => {
    clearHoverTimer()
    isHoveringRef.current = false
    setIsHovering(false)
    setCanShowVideo(false)
    pauseVideo()
  }

  const previewCard = () => {
    clearHoverTimer()
    isHoveringRef.current = false
    setIsHovering(false)
    setCanShowVideo(false)
    pauseVideo()
    onPreview(card)
  }

  useEffect(() => {
    return clearHoverTimer
  }, [])

  useEffect(() => {
    if (!shouldPlayVideo) return

    playVideo()
  }, [shouldPlayVideo])

  useEffect(() => {
    if (!shouldLoadVideo) return

    const video = videoRef.current

    if (!video) return

    if (video.readyState >= 2) {
      setIsVideoReady(true)
    }
  }, [shouldLoadVideo])

  useEffect(() => {
    if (card.videoCover || !card.videoUrl || shouldLoadVideo) return

    const target = cardRef.current

    if (!target) return

    if (!window.IntersectionObserver) {
      setShouldLoadVideo(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return

        setShouldLoadVideo(true)
        observer.disconnect()
      },
      {
        rootMargin: lazyLoadRootMargin,
      },
    )

    observer.observe(target)

    return () => observer.disconnect()
  }, [card.videoCover, card.videoUrl, shouldLoadVideo])

  return (
    <div
      className={styles.inspirationCard}
      id={getInspirationCardElementId(card.templateId)}
      ref={cardRef}
      onClick={previewCard}
      onMouseEnter={schedulePlayVideo}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={styles.cardMedia}
        style={{ aspectRatio: getTemplateRatio(card).replace(":", " / ") }}
      >
        {hasVideoCover ? (
          <img
            className={
              showCover ? styles.cardVideoCover : styles.cardMediaLayer
            }
            alt={card.title}
            src={card.videoCover}
            onError={() => {
              setCoverFailed(true)
              setShouldLoadVideo(true)
            }}
          />
        ) : null}
        {card.videoUrl && shouldLoadVideo ? (
          <video
            className={showVideo ? styles.cardVideo : styles.cardMediaLayer}
            controls={false}
            controlsList={restrictDownload ? "nodownload" : undefined}
            loop
            muted
            playsInline
            preload={isHovering ? "auto" : "metadata"}
            ref={videoRef}
            src={card.videoUrl}
            onContextMenu={(event) => {
              if (restrictDownload) event.preventDefault()
            }}
            onError={() => {
              setCanShowVideo(false)
              setIsVideoReady(false)
              setVideoFailed(true)
            }}
            onCanPlay={markVideoReady}
            onLoadedData={markVideoReady}
            onPlaying={markVideoReady}
          />
        ) : null}
        {badge ? (
          <Tag
            variant="solid"
            className={styles.cardBadge}
            color={badge === "热门" ? "primary" : "cyan"}
          >
            {badge === "热门" ? "🔥" : ""}
            {badge}
          </Tag>
        ) : null}
        <div className={styles.cardTitleOverlay}>
          <Button
            className={styles.overlayButton}
            color="primary"
            icon={<SparklesIcon className={styles.actionIcon} />}
            size="large"
            variant="outlined"
            shape="round"
            onClick={(event) => {
              event.stopPropagation()
              previewCard()
            }}
          >
            做同款
          </Button>
        </div>
      </div>
      <Flex
        className={styles.cardInfo}
        gap={8}
        justify="space-between"
        align="center"
      >
        <Text
          size="m"
          className={styles.templateTitle}
        >
          {card.title}
        </Text>
        <Text
          size="s"
          type="secondary"
          className={styles.duration}
        >
          {card.duration}秒
        </Text>
        {action ? (
          <span onClick={(event) => event.stopPropagation()}>{action}</span>
        ) : null}
      </Flex>
    </div>
  )
}

type InspirationGalleryProps = {
  cards?: VideoTemplate[]
  availableOnly?: boolean
  className?: string
  title?: ReactNode
  trackPreview?: boolean
  renderAction?: (card: VideoTemplate) => ReactNode
}

export const InspirationGallery = (props: InspirationGalleryProps) => {
  return props.cards ? (
    <GalleryContent {...props} />
  ) : (
    <TemplateGallery {...props} />
  )
}

const TemplateGallery = (props: InspirationGalleryProps) => {
  const {
    templates,
    status,
    error,
    reload,
    category,
    setCategory,
    hasMore,
    loadingMore,
    moreError,
    loadMore,
    retryMore,
  } = useTemplateGallery()
  return (
    <GalleryContent
      {...props}
      loading={status === "loading"}
      error={error}
      onReload={reload}
      hasMore={hasMore}
      footer={
        status === "ready" ? (
          <LoadMore
            count={templates.length}
            hasMore={hasMore}
            loading={loadingMore}
            error={moreError}
            onLoad={loadMore}
            onRetry={retryMore}
            onReload={reload}
          />
        ) : null
      }
      filters={
        <TemplateFilters
          selected={category}
          onChange={setCategory}
        />
      }
      cards={
        props.availableOnly
          ? templates.filter((template) => template.sameStyleAvailable)
          : templates
      }
    />
  )
}

type GalleryContentProps = InspirationGalleryProps & {
  filters?: ReactNode
  loading?: boolean
  error?: string
  onReload?: () => void
  hasMore?: boolean
  footer?: ReactNode
}

const GalleryContent = ({
  cards = [],
  className,
  title = "模板一键成片",
  trackPreview = true,
  renderAction,
  filters,
  loading = false,
  error = "",
  onReload,
  hasMore = false,
  footer,
}: GalleryContentProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const galleryRef = useRef<HTMLElement>(null)
  const columnCount = useInspirationColumnCount(galleryRef)
  const sendAnalytics = useAnalyticsEvent({ prefix: "灵感广场" })
  const [previewCardId, setPreviewCardId] = useState("")
  const previewCard = cards.find((card) => {
    return card.templateId === previewCardId
  })
  const showPreviewCard = (card: VideoTemplate) => {
    if (trackPreview) {
      sendAnalytics(`做同款`, getTemplateAnalyticsFields(card))
    }
    if (card.sameStyleAvailable) {
      navigate(getTemplateDetailPath(card.templateId), {
        state: { from: location.pathname },
      })
      return
    }
    setPreviewCardId(card.templateId)
  }
  const closePreviewModal = () => {
    setPreviewCardId("")
  }

  useEffect(() => {
    if (loading || error) return
    const result = consumeOneClickCopyRetryTransfer()

    if (result.status === "empty") return

    if (result.status === "invalid") {
      void message.warning("重试数据已失效，请从任务列表重新操作")
      return
    }

    navigate(getTemplateDetailPath(result.payload.templateValues.templateId), {
      replace: true,
      state: { retryDraft: result.payload },
    })
  }, [loading, error, navigate])

  return (
    <>
      <section
        ref={galleryRef}
        className={classNames(styles.inspirationSection, className)}
      >
        {title != null ? (
          <Text
            size={18}
            className={styles.sectionTitle}
            strong
          >
            {title}
          </Text>
        ) : null}
        {filters}
        <div
          className={filters ? styles.filterResults : undefined}
          aria-busy={loading}
        >
          {loading ? (
            <Flex
              justify="center"
              role="status"
              className={styles.listStatus}
            >
              <Spin description="加载模板中…" />
            </Flex>
          ) : error ? (
            <Result
              status="warning"
              title="模板加载失败"
              subTitle={error}
              extra={<Button onClick={onReload}>重试</Button>}
            />
          ) : cards.length ? (
            <Masonry
              className={styles.inspirationGrid}
              columns={columnCount}
              gutter={[CARD_GAP, CARD_GAP]}
              items={cards.map((card) => ({
                data: card,
                key: card.templateId,
              }))}
              itemRender={({ data: card }) => (
                <InspirationCardItem
                  card={card}
                  onPreview={showPreviewCard}
                  action={renderAction?.(card)}
                />
              )}
            />
          ) : hasMore ? null : (
            <Empty description="暂无符合条件的视频模板" />
          )}
          {footer}
        </div>
      </section>
      {previewCard ? (
        <PreviewModal
          card={previewCard}
          open
          onClose={closePreviewModal}
        />
      ) : null}
    </>
  )
}
